<template>
  <loading-radio-group
    v-if="inputLoading"
    :background="background"
    :options="options"
    :alt-bg="altBg"
  />
  <div v-else>
    <base-input-label v-if="required" :required="true" />
    <div :class="wrapClass">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="tw-flex tw-justify-between"
      >
        <label
          class="tw-group game-tile-radius tw-flex tw-items-center mod-button-transition tw-relative"
          :class="[
            inputBgStyles,
            disabled ? 'tw-cursor-default tw-opacity-75' : 'tw-cursor-pointer',
            small ? 'tw-text-sm' : 'mod-input--text-size',
            {
              'mod-input--px mod-input--min-height': background,
              'tw-capitalize': capsCase,
              'tw-w-full': !$slots.trailingInline,
            },
            errorStyles,
          ]"
        >
          <!-- hidden input is used for tabbing / focus -->
          <input
            class="tw-absolute tw-opacity-0 tw-size-0"
            :checked="modelValue === options[index]"
            type="radio"
            :name="`${inputId}RadioGroup`"
            :disabled="readonly"
            :tabindex="disabled ? '-1' : '0'"
            @click="updateCheckbox(index)"
          />

          <span
            class="tw-flex tw-items-center tw-justify-center tw-shrink-0 mod-button-transition tw-size-3 tw-rounded-full tw-mr-2"
            :class="[isChecked(index), hasHover(index)]"
          >
          </span>

          <span>{{ capsCase ? option.toLowerCase() : option }}</span>

          <slot name="right" :index="index" />

          <span
            v-if="appendMap?.[option]"
            class="tw-ml-auto tw-pl-1 tw-text-right tw-opacity-50"
          >
            {{ appendMap[option].toLocaleString() }}
          </span>
        </label>
        <slot
          v-if="$slots.trailingInline"
          name="trailingInline"
          :index="index"
        />
      </div>
    </div>

    <div
      v-if="hasErrors || hint"
      class="tw-flex tw-flex-col mod-hint-gap tw-space-y-1"
    >
      <base-input-errors :errors="errors" align="left" />

      <base-input-hint v-if="hint">
        {{ hint }}
      </base-input-hint>
    </div>
  </div>
</template>

<script>
import LoadingRadioGroup from '@components/Loading/LoadingRadioGroup.vue'
import { computed, toRefs, inject } from 'vue'
import { genHtmlId } from '@helpers/utils.js'
export default {
  components: {
    LoadingRadioGroup,
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    appendMap: {
      type: Object,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    errors: {
      type: Array,
      default: () => [],
    },
    background: {
      type: Boolean,
      default: false,
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    wrapClass: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    capsCase: {
      type: Boolean,
      default: false,
    },
    altBg: {
      type: Boolean,
      default: false,
    },
    sidebar: {
      type: Boolean,
      default: false,
    },
    hoverHighlight: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const {
      hoverHighlight,
      background,
      modelValue,
      disabled,
      options,
      sidebar,
      errors,
      altBg,
      id,
    } = toRefs(props)

    const inputId = id.value ? id.value : genHtmlId()
    const readonly = inject('saving', false)

    const hasErrors = computed(() => !!errors.value?.length)

    const errorStyles = computed(() => {
      if (hasErrors.value) {
        return background.value
          ? 'tw-border-danger tw-border-2'
          : 'tw-text-danger'
      }
      return ''
    })

    const inputBgStyles = computed(() => {
      if (disabled.value) {
        return background.value ? 'game-input-background-color' : ''
      }
      return background.value
        ? 'game-input-background-color mod-input--focus-within'
        : 'tw-opacity-90 hover:tw-opacity-100 focus-within:game-link-color'
    })

    function hasHover(index) {
      if (
        disabled.value ||
        modelValue.value === options.value[index] ||
        !hoverHighlight.value
      ) {
        return ''
      }

      if (altBg.value) {
        return background.value ? '' : 'game-input-background-color'
      } else {
        return background.value ? '' : 'game-input-background-color'
      }
    }

    function isChecked(index) {
      if (modelValue.value === options.value[index]) {
        return 'game-primary-button-color game-link-color'
      } else if (altBg.value) {
        return background.value
          ? ''
          : `${
              sidebar.value ? 'tw-mobile-grey' : 'tw-bg-theme'
            } tw-opacity-40 group-hover:tw-opacity-100`
      } else {
        return 'tw-bg-[var(--input-background-alt-color)]'
      }
    }

    function updateCheckbox(value) {
      if (!disabled.value) {
        emit('input', value)
      }
    }

    return {
      updateCheckbox,
      inputBgStyles,
      errorStyles,
      hasErrors,
      isChecked,
      hasHover,
      readonly,
      inputId,
    }
  },
}
</script>
